import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaPlus, FaSearch } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { graphqlRequest } from '../../../utils/graphqlRequest';
import { formatAUD } from '../../../utils/formatCurrency';
import { useAdminPageSearch } from '../../../utils/useAdminPageSearch';
import LogUsageModal from './LogUsageModal';
import '../styles/InventoryEquipment.css';
import '../styles/InventoryUsage.css';

const USAGE = `
  query FindAllResourceUsage {
    findAllResourceUsage {
      id
      usageDate
      projectId
      jobNumber
      site
      employeeName
      itemId
      productName
      qtyUsed
      expectedQty
      variance
      cost
      status
    }
  }
`;

const INVENTORY = `
  query UsageExpectedInventory {
    findAllInventoryManagement {
      quantityAssigned
      tool { id }
      project { id }
    }
  }
`;

function formatDate(raw) {
  if (!raw) return '—';
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function isSameDay(raw, date = new Date()) {
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return false;
  return (
    d.getFullYear() === date.getFullYear() &&
    d.getMonth() === date.getMonth() &&
    d.getDate() === date.getDate()
  );
}

function statusMeta(status) {
  const key = String(status || 'Normal');
  if (key === 'Excessive') {
    return { key: 'excessive', label: 'Excessive' };
  }
  if (key === 'HighUsage' || key === 'HIGH_USAGE') {
    return { key: 'high', label: 'High Usage' };
  }
  return { key: 'normal', label: 'Normal' };
}

function formatVariance(n) {
  const v = Number(n) || 0;
  if (v > 0) return `+${v}`;
  return String(v);
}

export default function UsageTrackingDashboard() {
  const navigate = useNavigate();
  const { get, patch } = useAdminPageSearch();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [siteFilter, setSiteFilter] = useState('');
  const [employeeFilter, setEmployeeFilter] = useState('');
  const [productFilter, setProductFilter] = useState('');
  const [expectedByItem, setExpectedByItem] = useState({});
  const showLog = get('action') === 'add';

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [usageRes, invRes] = await Promise.all([
        graphqlRequest(USAGE).catch(() => ({})),
        graphqlRequest(INVENTORY).catch(() => ({})),
      ]);
      setRows(
        Array.isArray(usageRes?.findAllResourceUsage)
          ? usageRes.findAllResourceUsage
          : [],
      );
      const map = {};
      (Array.isArray(invRes?.findAllInventoryManagement)
        ? invRes.findAllInventoryManagement
        : []
      ).forEach((m) => {
        if (!m?.tool?.id) return;
        const qty = Number(m.quantityAssigned) || 0;
        if (m.project?.id) {
          map[`${m.project.id}:${m.tool.id}`] = qty;
        }
        if (map[m.tool.id] == null) map[m.tool.id] = qty;
      });
      setExpectedByItem(map);
    } catch (err) {
      toast.error(err?.message || 'Failed to load usage records');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const sites = useMemo(
    () => [...new Set(rows.map((r) => r.site).filter(Boolean))].sort(),
    [rows],
  );
  const employees = useMemo(
    () => [...new Set(rows.map((r) => r.employeeName).filter(Boolean))].sort(),
    [rows],
  );
  const products = useMemo(
    () => [...new Set(rows.map((r) => r.productName).filter(Boolean))].sort(),
    [rows],
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (siteFilter && r.site !== siteFilter) return false;
      if (employeeFilter && r.employeeName !== employeeFilter) return false;
      if (productFilter && r.productName !== productFilter) return false;
      if (!q) return true;
      const hay = `${r.jobNumber || ''} ${r.site || ''} ${r.employeeName || ''} ${
        r.productName || ''
      }`.toLowerCase();
      return hay.includes(q);
    });
  }, [rows, search, siteFilter, employeeFilter, productFilter]);

  const excessiveToday = useMemo(
    () =>
      rows.filter(
        (r) => r.status === 'Excessive' && isSameDay(r.usageDate),
      ).length,
    [rows],
  );

  const openJob = (jobNumber) => {
    if (!jobNumber) return;
    navigate(
      { pathname: '/', search: `?page=allocation&job=${encodeURIComponent(jobNumber)}` },
      { replace: true },
    );
  };

  return (
    <div className="iut-page">
      <header className="iut-header">
        <h1>Resource Usage Tracking</h1>
        <button
          type="button"
          className="ieq-btn ieq-btn--primary"
          onClick={() => patch({ action: 'add' })}
        >
          <FaPlus /> Log Usage
        </button>
      </header>

      {excessiveToday > 0 && (
        <div className="iut-alert" role="status">
          ⚠️ {excessiveToday} usage record{excessiveToday === 1 ? '' : 's'} show{' '}
          <strong>excessive variance</strong> today — review highlighted rows below.
        </div>
      )}

      <div className="iut-toolbar">
        <label className="iut-search">
          <FaSearch />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search..."
          />
        </label>
        <select
          value={siteFilter}
          onChange={(e) => setSiteFilter(e.target.value)}
          aria-label="Filter by site"
        >
          <option value="">Site</option>
          {sites.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        <select
          value={employeeFilter}
          onChange={(e) => setEmployeeFilter(e.target.value)}
          aria-label="Filter by employee"
        >
          <option value="">Employee</option>
          {employees.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        <select
          value={productFilter}
          onChange={(e) => setProductFilter(e.target.value)}
          aria-label="Filter by product"
        >
          <option value="">Product</option>
          {products.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </div>

      <div className="iut-table-wrap">
        {loading ? (
          <div className="iut-empty">Loading usage records…</div>
        ) : filtered.length === 0 ? (
          <div className="iut-empty">
            No usage logs yet. Click + Log Usage to record product use on a job.
          </div>
        ) : (
          <table className="iut-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Job #</th>
                <th>Site</th>
                <th>Employee</th>
                <th>Product Used</th>
                <th>Qty Used</th>
                <th>Expected Qty</th>
                <th>Variance</th>
                <th>Cost</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((row) => {
                const st = statusMeta(row.status);
                const v = Number(row.variance) || 0;
                return (
                  <tr
                    key={row.id}
                    className={
                      st.key === 'excessive'
                        ? 'iut-row--excessive'
                        : st.key === 'high'
                          ? 'iut-row--high'
                          : ''
                    }
                  >
                    <td>{formatDate(row.usageDate)}</td>
                    <td>
                      {row.jobNumber ? (
                        <button
                          type="button"
                          className="iut-job"
                          onClick={() => openJob(row.jobNumber)}
                        >
                          {row.jobNumber}
                        </button>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td>{row.site || '—'}</td>
                    <td>{row.employeeName || '—'}</td>
                    <td>{row.productName}</td>
                    <td>{Number(row.qtyUsed)}</td>
                    <td>{Number(row.expectedQty)}</td>
                    <td
                      className={
                        v > 0 ? 'iut-var iut-var--up' : v < 0 ? 'iut-var iut-var--down' : ''
                      }
                    >
                      {formatVariance(row.variance)}
                    </td>
                    <td>{formatAUD(row.cost)}</td>
                    <td>
                      <span className={`iut-pill iut-pill--${st.key}`}>{st.label}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <div className="iut-legend">
        <span>
          <i className="iut-pill iut-pill--normal">Normal</i>
        </span>
        <span>
          <i className="iut-pill iut-pill--high">High Usage</i> &gt;1.5× expected
        </span>
        <span>
          <i className="iut-pill iut-pill--excessive">Excessive</i> &gt;2× expected
        </span>
      </div>

      {showLog && (
        <LogUsageModal
          expectedByItem={expectedByItem}
          onClose={() => patch({}, ['action'])}
          onSaved={load}
        />
      )}
    </div>
  );
}
